import maEventBus from '@/components/eventbus/index.js'

// components
import maAccordion from '@/components/accordion'
import maInputText from '@/components/input/modules/text'
import maModal from '@/components/modal'
import maInputSelect from '@/components/input/modules/select'

// constants
import { MA_WARRANTY_WATER_MOBILIER } from '@/constants'

// api
import { apiCreateSinister } from '@/services/API/createSinister'
import { apiGetGenericFormsList, apiGetFnolIds } from '@/services/API/menus'
import { apiPostTrackTMO } from '@/services/API/timeStamp'
import { apiGetSinister } from '@/services/API/sinister'

import setTimeStamp from '@/utils/timeStamp.js'

// types
import { mapState, mapMutations, mapActions } from 'vuex'

import {
  WARRANTY_MODULE_NAME,
  WARRANTY_UPDATE_SINISTER_ID,
  WARRANTY_UPDATE_SECONDARY_ID,
  WARRANTY_UPDATE_CREATED,
  WARRANTY_CLEAR_MODULE,
  WARRANTY_GET_SINISTER,
  WARRANTY_UPDATE_ZIP_CODE,
  WARRANTY_EXISTING_SINISTER
} from '@/store/types'

/**
 * @module
 * @name warrantyRef
 */
export default {
  name: 'warrantyRef',
  data: function() {
    return {
      linkSelfCare: 'linkSelfCare',
      showLinkSelfcare: false,
      selfcareForms: [],
      selfcareFormsValue: null,
      selfcareFnolIds: [],
      selfcareFnolIdsValue: null,
      selfcareUsers: [],
      selfcareUsersValue: null
    }
  },
  components: {
    maInputText,
    maAccordion,
    maModal,
    maInputSelect
  },

  computed: {
    ...mapState(WARRANTY_MODULE_NAME, {
      stateWarrantySinisterId: 'sinisterId',
      stateWarrantySecondaryId: 'secondaryId',
      stateWarrantyCreated: 'created',
      stateWarrantyZipCode: 'zipCode',
      stateExistingSinister: 'existingSinister'
    }),
    stateComWarrantySinisterId: {
      get() {
        return this.stateWarrantySinisterId
      },
      set(v) {
        this.mutWarrantyUpdateSinisterId(v)
      }
    },
    stateComWarrantySecondaryId: {
      get() {
        return this.stateWarrantySecondaryId
      },
      set(v) {
        this.mutWarrantyUpdateSecondaryId(v)
      }
    },
    stateComWarrantyZipCode: {
      get() {
        return this.stateWarrantyZipCode
      },
      set(v) {
        this.mutWarrantySetZipCode(v)
      }
    },
    comWarrantyCreated: {
      get() {
        return this.stateWarrantyCreated
      },
      set(v) {
        this.mutWarrantyUpdateCreated(v)
      }
    },
    isMobilierRoute() {
      return this.$route.name === MA_WARRANTY_WATER_MOBILIER
    },
    shouldCloseAccordion: function() {
      return this.FNOLFlow.params.closeAccordions
        ? this.FNOLFlow.params.closeAccordions
        : false
    }
  },

  methods: {
    ...mapActions(WARRANTY_MODULE_NAME, {
      actWarrantyGetSinister: WARRANTY_GET_SINISTER
    }),
    ...mapMutations(WARRANTY_MODULE_NAME, {
      mutWarrantyUpdateSinisterId: WARRANTY_UPDATE_SINISTER_ID,
      mutWarrantyUpdateSecondaryId: WARRANTY_UPDATE_SECONDARY_ID,
      mutWarrantySetZipCode: WARRANTY_UPDATE_ZIP_CODE,
      mutWarrantyUpdateCreated: WARRANTY_UPDATE_CREATED,
      mutWarrantyClearModule: WARRANTY_CLEAR_MODULE,
      mutWarrantyExistingSinister: WARRANTY_EXISTING_SINISTER
    }),

    hideUrl() {
      this.showLinkSelfcare = false
    },

    /**
     * @function
     * @name navToProps
     */
    navToProps: function() {
      this.$refs.refAccordion.hideContent()
      maEventBus.$emit('props:expand')
    },

    /**
     * @function
     * @name setSinisterRefsFromFNOLFlow
     * @description Set url query params
     */
    setSinisterRefsFromFNOLFlow: function() {
      if (!this.FNOLFlow.isExecuting) return

      if (this.FNOLFlow.params.refSin) {
        this.mutWarrantyUpdateSinisterId(this.FNOLFlow.params.refSin)
        this.sendTMOTrace(this.FNOLFlow.params.refSin)
      }

      if (this.FNOLFlow.params.refEvt) {
        this.mutWarrantyUpdateSecondaryId(this.FNOLFlow.params.refEvt)
      }
    },

    setSelfcareForms: function() {
      return new Promise((resolve, reject) => {
        apiGetGenericFormsList().then(resp => {
          if (resp && resp.length > 0) {
            this.selfcareForms = resp
            this.selfcareFormsValue = resp[0]['key']
          }
          resolve(resp)
        })
      })
    },

    setSelfcareFnolIds() {
      return new Promise((resolve, reject) => {
        apiGetFnolIds().then(resp => {
          if (resp && resp.length > 0) {
            this.selfcareFnolIds = [
              { key: null, literal: this.$t('common.noSelected') },
              ...resp
            ]
            this.selfcareFnolIdsValue = this.selfcareFnolIds[0].key

            this.selfcareUsers = [...this.userTypeOptions]
            this.selfcareUsersValue = this.selfcareUsers[0].key
          }
          resolve(resp)
        })
      })
    },

    async handleOnChangeSinisterId(event) {
      let existingSinister = false
      this.mutWarrantyUpdateSinisterId(this.removeSinisterSpaces(event))
      try {
        existingSinister = await apiGetSinister(this.stateComWarrantySinisterId)
      } catch (error) {
        console.error(error)
      }
      if (!!existingSinister === false) {
        this.sendTMOTrace(this.stateComWarrantySinisterId)
        this.mutWarrantyExistingSinister(false)
      } else {
        this.mutWarrantyExistingSinister(true)
        this.throwNotification(
          this.$t('notifications.existingSinister.title'),
          this.$t('notifications.existingSinister.body'),
          'KO'
        )
      }
      this.mutWarrantyUpdateSinisterId(this.removeSinisterSpaces(event))
    },

    handleOnBlurSinisterId(event) {
      this.mutWarrantyUpdateSinisterId(this.removeSinisterSpaces(event))
    },

    removeSinisterSpaces (value) {
      return value.includes(' ') ? value.replace(/\s+/g, '') : value
    },

    sendTMOTrace(sinisterId) {
      if (sinisterId != null && sinisterId !== '') {
        const timestamp = setTimeStamp.call()
        const timeStampParams = {
          description: `Introducing sinister - ${sinisterId}`,
          sinister_id: sinisterId,
          start: timestamp,
          end: timestamp
        }
        apiPostTrackTMO(timeStampParams).catch(() => {
          throw new Error('Error tracking "Introducing sinister"')
        })
      }
    },

    handleOpenModal() {
      Promise.all([this.setSelfcareForms(), this.setSelfcareFnolIds()]).then(
        () => {
          if (this.selfcareFormsValue && this.selfcareFnolIds.length > 0) {
            this.$refs.linkModal.showModal = true
          }
        }
      )
    },

    /**
     * @function
     * @name handleGenerateLink
     * @description If the details of the sinister are filled it calls the create sinister api and generates a link for the self care app
     */
    handleGenerateLink() {
      if (this.stateComWarrantySinisterId) {
        const defaultWarrantyValue = 'DDE'
        apiCreateSinister({
          sinister_id: this.stateComWarrantySinisterId,
          event_id: this.stateWarrantySecondaryId,
          form_id: this.selfcareFormsValue,
          zip_code: this.stateWarrantyZipCode,
          id_flux: this.selfcareFnolIdsValue,
          warranty: defaultWarrantyValue
        }).then(res => {
          this.showLinkSelfcare = true
          this.linkSelfCare = res.url
        })
      } else {
        this.throwNotification(
          this.$t(`notifications.error.${400}.title`),
          this.$t(`notifications.error.${400}.body`),
          'KO'
        )
      }
    },

    /**
     * @function
     * @name cancelGenerateLink
     * @description clears the link properties and closes modal
     */
    cancelGenerateLink() {
      this.showLinkSelfcare = false
      this.linkSelfCare = ''
    },

    closeLinkModal() {
      this.$refs.linkModal.closeModal()
      this.showLinkSelfcare = false
      // this.resetSelections()
    },

    resetSelections() {
      this.linkSelfCare = ''
      this.selfcareFnolIds = []
      this.selfcareForms = []
      this.selfcareFnolIdsValue = ''
      this.selfcareFormsValue = ''
      this.selfcareUsers = []
      this.selfcareUsersValue = null
    },

    /**
     * @function
     * @name watchStateWarrantySinisterId
     * @description Watcher used for sinister Id. When it changes, all data is
     *              cleared.
     */
    watchStateWarrantySinisterId: function() {
      if (this.comWarrantyCreated) {
        this.comWarrantyCreated = false
        this.throwNotification(
          this.$t('notifications.newSinister.title'),
          this.$t('notifications.newSinister.body'),
          'OK'
        )
        const sinisterId = this.stateWarrantySinisterId
        this.mutWarrantyClearModule()
        if (sinisterId && sinisterId.length > 0) {
          this.mutWarrantyUpdateSinisterId(sinisterId)
        }
      }
    },

    /**
     * @function
     * @name watchStateWarrantySecondaryId
     * @description Watcher used for secondary Id. When it changes, all data is
     *              cleared.
     */
    watchStateWarrantySecondaryId: function() {
      if (this.comWarrantyCreated) {
        this.comWarrantyCreated = false
        this.throwNotification(
          this.$t('notifications.newSinister.title'),
          this.$t('notifications.newSinister.body'),
          'OK'
        )
      }
    },

    setWatchers: function() {
      this.$watch('stateWarrantySinisterId', this.watchStateWarrantySinisterId)
      this.$watch(
        'stateWarrantySecondaryId',
        this.watchStateWarrantySecondaryId
      )
    }
  },

  /**
   * @function
   * @name created
   * @description Init FNOL Flow, set sinister id and event id.
   */
  created: function() {
    this.setSinisterRefsFromFNOLFlow()
  },

  /**
   * @function
   * @name mounted
   * @decription Management of edit sinister.
   */
  mounted: function() {
    const sinisterId =
      this.$route.params.sinisterId || this.FNOLFlow.params.refSin
    const complementaryRef =
      this.$route.params.complementaryRef || this.FNOLFlow.params.refEvt
    if (sinisterId) {
      this.actWarrantyGetSinister(sinisterId)
        .then(response => {
          if (response.isSinisterFound) {
            if (this.FNOLFlow.isExecuting) {
              maEventBus.$emit('props:initializeFromFNOL')
            }
            this.throwNotification(
              this.$t('notifications.loadSinisterOK.title'),
              this.$t('notifications.loadSinisterOK.body'),
              'OK'
            )
          }
          if (complementaryRef) {
            this.mutWarrantyUpdateSecondaryId(complementaryRef)
          }
          this.setWatchers()
        })
        .catch(() => {
          this.throwNotification(
            this.$t('notifications.loadSinisterKO.title'),
            this.$t('notifications.loadSinisterKO.body'),
            'KO'
          )
        })
    } else {
      this.setWatchers()
    }

    // init handler for input forms
    document
      .querySelectorAll(
        '.warrantyRef .accordion__bottom .input:last-child input'
      )
      .forEach(el => {
        el.addEventListener('keydown', e => {
          if (e.keyCode === 9) {
            this.navToProps()
          }
        })
      })
  }
}
